import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "../mobile.css";

// Open panels, oldest first: Escape closes only the top one (a dialog over a task sheet must not
// close the sheet too), and none while a Bootstrap modal (the PIN sheet, the cause picker) is up.
const openPanels = [];

/**
 * A screen that slides over everything (plan section 4: replaces hand-rolled position:fixed
 * modals and, on phone screens, native alert/confirm/prompt). Title bar with a close button,
 * a scrolling body, an optional footer that stays at the bottom (above the home bar).
 * Escape closes it; the page under it does not scroll while it is open.
 */
export default function FullScreenPanel({
  open,
  title,
  onClose,
  children,
  footer = null,
  closeLabel = "Close",
  testId = "fullscreen-panel",
}) {
  const bodyRef = useRef(null);
  const closeRef = useRef(null);
  // The latest onClose, read when Escape is pressed: a caller that passes a new function (or undefined
  // while it saves) must not re-run the open effect, which would move focus to the close button and
  // drop the phone's keyboard mid-typing.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const me = {};
    openPanels.push(me);
    const onKey = (e) => {
      if (e.key !== "Escape" || !onCloseRef.current) return;
      if (openPanels[openPanels.length - 1] !== me || document.querySelector(".modal.show")) return;
      onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => { if (closeRef.current) closeRef.current.focus(); }, 0);
    return () => {
      clearTimeout(t);
      const i = openPanels.indexOf(me);
      if (i >= 0) openPanels.splice(i, 1);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="jq-panel" role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined} data-testid={testId}>
      <div className="jq-panel-header">
        <h2 className="jq-panel-title">{title}</h2>
        {onClose && (
          <button ref={closeRef} type="button" className="jq-icon-btn" onClick={onClose} aria-label={closeLabel}>
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="jq-panel-body" ref={bodyRef}>{children}</div>
      {footer && <div className="jq-panel-footer">{footer}</div>}
    </div>,
    document.body
  );
}
